import { jsx } from 'react/jsx-runtime';
import { forwardRef, useContext } from 'react';
import styled from 'styled-components';
import { buttonStyles, defaultStyles, hoverStyles, focusStyles } from './setLinkStyles.js';
import { NicolletContext } from '../NicolletContext/index.js';
import { AppContext } from '../AppContext/index.js';

const Link = forwardRef(({
  href,
  color,
  underline,
  onClick,
  children,
  className,
  target,
  noHoverColor,
  refresh = false,
  // Extract Next.js Link props so we don't forward to anchors or buttons, only to `RouterLink`.
  prefetch,
  replace,
  scroll,
  shallow,
  // NOTE: Any props destructured here going forward may need to be added to `anchorProps` below to propagate.
  ...restProps
}, ref) => {
  // So `NicolletContext` is deprecated in favor of `AppContext`,
  // but some places like AdaptiveUI are still using it, so we'll check both here.
  const {
    nicollet = {}
  } = useContext(NicolletContext);
  const appContext = useContext(AppContext.Context);
  const {
    isRouterLinkNextLink
  } = appContext; // In Web Platform sites, RouterLink is passed as a Next.js `next/link`.
  // TODO: Once all Web Platform foundation is providing `isRouterLinkNextLink: true` and all sites have upgraded,
  // we can consume from `appContext` either way. Until then it's not safe, so use legacy context.

  const {
    RouterLink = 'a'
  } = isRouterLinkNextLink ? appContext : nicollet;
  const hrefTrimmed = href === null || href === void 0 ? void 0 : href.trim(); // regular anchor tag if no routing required (href = #) for optimization.
  // regular anchor tag if absolute url starting with protocol example http:

  const isAnchor = hrefTrimmed === null || hrefTrimmed === void 0 ? void 0 : hrefTrimmed.match(/^(#|tel:|mailto:|http)/);
  const anchorProps = {
    href: hrefTrimmed,
    className,
    onClick,
    ref,
    target,
    ...restProps
  };

  if (!hrefTrimmed) {
    return (// We're passing a bunch of <a> properties into a <button>.
      // TODO: Let's break this into a separate component (ButtonAsLink?), so we don't have to do that
      // @ts-ignore
      jsx("button", Object.assign({}, restProps, {
        className: className,
        onClick: onClick,
        ref: ref,
        type: "button"
      }, {
        children: children
      }), void 0)
    );
  }

  if (!isAnchor && RouterLink) {
    return isRouterLinkNextLink ? // In Web Platform sites, `RouterLink` is provided as a Next.js `next/link`,
    // so we need to wrap the children in an anchor tag internally.
    jsx(RouterLink, Object.assign({
      href: hrefTrimmed,
      // Add the Next.js Link props back for forwarding to `RouterLink`.
      prefetch: prefetch,
      replace: replace,
      scroll: scroll,
      shallow: shallow
    }, {
      children: jsx("a", Object.assign({}, anchorProps, {
        children: children
      }), void 0)
    }), void 0) : // In AdaptiveUI, `RouterLink` is provided as a React Router link, where the anchor tag is rendered
    // as part of the framework. Not sure about other consumers or platforms, but not changing behavior.
    jsx(RouterLink, Object.assign({}, anchorProps, {
      to: hrefTrimmed
    }, {
      children: children
    }), void 0);
  }

  return jsx("a", Object.assign({}, anchorProps, {
    children: children
  }), void 0);
});
Link.displayName = 'Link';
const StyledLink = styled(Link)`
  ${buttonStyles};
  ${defaultStyles};
  ${hoverStyles};
  ${focusStyles};
`;
StyledLink.displayName = 'Link';
StyledLink.defaultProps = {
  color: 'onLight',
  noHoverColor: false,
  refresh: false,
  underline: 'default'
};

export { StyledLink as Link };
